import * as React from 'react';
import { showFavoritesFoldersList, showModalCreateFolder } from '@cian/favorites-assign-widget';
import { hideNotification } from '@cian/favorites-assign-widget/es/shared/actions';
import { useDeviceType } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { EAlertType, requestAuthentication } from '../../actions/authentication';
import { FavoriteButton, FavoriteTooltip } from '../../components/RedesignedFavoriteButtonDesktop';
import { addToFavorite, deleteFromFavorite, isAddToFavoriteRequestError } from '../../thunk/favorites';
import { selectIsAuthenticated } from '../../selectors/user';
import { selectRecommendationById } from '../../selectors/recommendations/selectRecommendationById';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { trackFavoritesLinkClick } from './tracking';

const TOOLTIP_CLOSE_TIMEOUT = 6000;

type TRedesignedFavoriteButtonDesktopContainerProps = {
  offerId: number;
  position: number;
};

export const RedesignedFavoriteButtonDesktopContainer: React.FC<TRedesignedFavoriteButtonDesktopContainerProps> = ({
  offerId,
  position,
}) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const [isErrorTooltipOpen, setErrorTooltipOpen] = React.useState(false);
  const favoriteActionVersionRef = React.useRef(0);
  const recommendation = useSelector(selectRecommendationById(offerId));
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isFavoriteUpdatePending = useSelector((state: IApplicationState) =>
    Boolean(state.recommendations.favoriteUpdateRequestIds?.[offerId]),
  );
  const { savedOfferId, showNotification, userHasFolders } = useSelector(
    (state: IApplicationState) => state.favoritesAssignWidget,
  );

  const isSuccessTooltipOpen = Boolean(
    recommendation.offerId && showNotification && savedOfferId.length === 1 && savedOfferId[0] === offerId,
  );
  const isTooltipOpen = isSuccessTooltipOpen || isErrorTooltipOpen;

  const handleCloseTooltip = React.useCallback(() => {
    if (isErrorTooltipOpen) {
      setErrorTooltipOpen(false);

      return;
    }

    if (isSuccessTooltipOpen) {
      dispatch(hideNotification());
    }
  }, [dispatch, isErrorTooltipOpen, isSuccessTooltipOpen]);

  React.useEffect(() => {
    if (!isTooltipOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleCloseTooltip();
    }, TOOLTIP_CLOSE_TIMEOUT);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [handleCloseTooltip, isTooltipOpen]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (!recommendation.offerId || isFavoriteUpdatePending) {
        return;
      }

      const actionVersion = favoriteActionVersionRef.current + 1;
      favoriteActionVersionRef.current = actionVersion;

      if (recommendation.isFavorite) {
        setErrorTooltipOpen(false);
        void dispatch(deleteFromFavorite({ deviceType, position, recommendation })).catch(() => undefined);

        return;
      }

      setErrorTooltipOpen(false);

      void dispatch(addToFavorite({ deviceType, position, recommendation })).catch((error: unknown) => {
        if (favoriteActionVersionRef.current === actionVersion && isAddToFavoriteRequestError(error)) {
          setErrorTooltipOpen(true);
        }
      });
    },
    [deviceType, dispatch, isFavoriteUpdatePending, position, recommendation],
  );

  const handleWrapperClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleAddToFolder = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      dispatch(hideNotification());

      if (!recommendation.offerId) {
        return;
      }

      if (!isAuthenticated) {
        dispatch(requestAuthentication({ alert: EAlertType.Favorites }));

        return;
      }

      if (userHasFolders) {
        dispatch(showFavoritesFoldersList({ offerIds: [recommendation.offerId] }));

        return;
      }

      dispatch(showModalCreateFolder({ offerIds: [recommendation.offerId] }));
    },
    [dispatch, isAuthenticated, recommendation.offerId, userHasFolders],
  );

  const handleFavoritesLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      trackFavoritesLinkClick(offerId);
    },
    [offerId],
  );

  if (!recommendation.offerId) {
    return null;
  }

  return (
    <div onClick={handleWrapperClick}>
      <FavoriteTooltip
        isOpen={isTooltipOpen}
        variant={isErrorTooltipOpen ? 'error' : 'success'}
        onAddToFolder={handleAddToFolder}
        onClose={handleCloseTooltip}
        onFavoritesLinkClick={handleFavoritesLinkClick}
      >
        <FavoriteButton isActive={recommendation.isFavorite} onClick={handleClick} />
      </FavoriteTooltip>
    </div>
  );
};
